import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Calendar, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { anoLetivoService } from '../services/anoLetivoService';
import type { AnoLetivo, CreateAnoLetivoDto, UpdateAnoLetivoDto } from '../services/anoLetivoService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  padding: 24px 24px 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: #f8fafc;
  color: #64748b;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #374151;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 16px;
    height: 16px;
    color: #6b7280;
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ef4444' : '#3b82f6'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorMessage = styled.span`
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const ToggleButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: none;
  background: ${props => props.isActive ? '#10b981' : '#6b7280'};
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.isActive ? '#059669' : '#4b5563'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ModalFooter = styled.div`
  padding: 0 24px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;

    &:hover:not(:disabled) {
      background: #2563eb;
      border-color: #2563eb;
    }

    &:disabled {
      background: #9ca3af;
      border-color: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #374151;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface AnoLetivoFormModalProps {
  anoLetivo?: AnoLetivo | null;
  onClose: () => void;
  onSubmit: (success: boolean) => void;
}

interface FormErrors {
  ano?: string;
  ativo?: string;
  aceitaMatriculas?: string;
  aceitaRematriculas?: string;
}

const AnoLetivoFormModal: React.FC<AnoLetivoFormModalProps> = ({
  anoLetivo,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    ano: new Date().getFullYear(),
    ativo: true,
    aceitaMatriculas: true,
    aceitaRematriculas: true
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const isEditing = !!anoLetivo;

  useEffect(() => {
    if (anoLetivo) {
      setFormData({
        ano: anoLetivo.ano,
        ativo: anoLetivo.ativo,
        aceitaMatriculas: anoLetivo.aceitaMatriculas,
        aceitaRematriculas: anoLetivo.aceitaRematriculas
      });
    }
  }, [anoLetivo]);

  const validateForm = async (): Promise<boolean> => {
    const newErrors: FormErrors = {};

    // Validação do ano
    if (!formData.ano) {
      newErrors.ano = 'Ano é obrigatório';
    } else if (formData.ano < 2020 || formData.ano > 2999) {
      newErrors.ano = 'Ano deve estar entre 2020 e 2999';
    } else {
      // Verificar se ano já existe
      const anoExists = await anoLetivoService.validarAno(
        formData.ano,
        anoLetivo?.id
      );
      if (!anoExists) {
        newErrors.ano = 'Este ano já está cadastrado';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading) return;

    const isValid = await validateForm();
    if (!isValid) return;

    setLoading(true);

    try {
      if (isEditing) {
        const updateData: UpdateAnoLetivoDto = {
          ano: formData.ano,
          ativo: formData.ativo,
          aceitaMatriculas: formData.aceitaMatriculas,
          aceitaRematriculas: formData.aceitaRematriculas
        };

        await anoLetivoService.atualizarAnoLetivo(anoLetivo!.id, updateData);
        toast.success('Ano letivo atualizado com sucesso!');
      } else {
        const createData: CreateAnoLetivoDto = {
          ano: formData.ano,
          ativo: formData.ativo,
          aceitaMatriculas: formData.aceitaMatriculas,
          aceitaRematriculas: formData.aceitaRematriculas
        };

        await anoLetivoService.criarAnoLetivo(createData);
        toast.success('Ano letivo criado com sucesso!');
      }

      onSubmit(true);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar ano letivo');
      onSubmit(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            <Calendar />
            {isEditing ? 'Editar Ano Letivo' : 'Novo Ano Letivo'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="ano">
                <Calendar />
                Ano
              </Label>
              <Input
                id="ano"
                type="number"
                placeholder="Digite o ano"
                value={formData.ano}
                onChange={(e) => handleInputChange('ano', parseInt(e.target.value) || 0)}
                hasError={!!errors.ano}
                min="2020"
                max="2999"
              />
              {errors.ano && <ErrorMessage>{errors.ano}</ErrorMessage>}
              <HelperText>Exemplo: 2024</HelperText>
            </FormGroup>

            <FormGroup>
              <ToggleContainer>
                <ToggleLabel>Ativo</ToggleLabel>
                <ToggleButton
                  type="button"
                  isActive={formData.ativo}
                  onClick={() => handleInputChange('ativo', !formData.ativo)}
                >
                  {formData.ativo ? <ToggleRight /> : <ToggleLeft />}
                  {formData.ativo ? 'Sim' : 'Não'}
                </ToggleButton>
              </ToggleContainer>
              <HelperText>Define se o ano letivo está ativo no sistema</HelperText>
            </FormGroup>

            <FormGroup>
              <ToggleContainer>
                <ToggleLabel>Aceita Matrículas</ToggleLabel>
                <ToggleButton
                  type="button"
                  isActive={formData.aceitaMatriculas}
                  onClick={() => handleInputChange('aceitaMatriculas', !formData.aceitaMatriculas)}
                >
                  {formData.aceitaMatriculas ? <ToggleRight /> : <ToggleLeft />}
                  {formData.aceitaMatriculas ? 'Sim' : 'Não'}
                </ToggleButton>
              </ToggleContainer>
              <HelperText>Permite novas matrículas neste ano letivo</HelperText>
            </FormGroup>

            <FormGroup>
              <ToggleContainer>
                <ToggleLabel>Aceita Rematrículas</ToggleLabel>
                <ToggleButton
                  type="button"
                  isActive={formData.aceitaRematriculas}
                  onClick={() => handleInputChange('aceitaRematriculas', !formData.aceitaRematriculas)}
                >
                  {formData.aceitaRematriculas ? <ToggleRight /> : <ToggleLeft />}
                  {formData.aceitaRematriculas ? 'Sim' : 'Não'}
                </ToggleButton>
              </ToggleContainer>
              <HelperText>Permite rematrículas neste ano letivo</HelperText>
            </FormGroup>
          </Form>
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading && <LoadingSpinner />}
            {isEditing ? 'Atualizar' : 'Criar'}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default AnoLetivoFormModal;
